<template>
  <div class="warranty-report-summary" @keyup.enter="onFilter">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    ></Loader>
    <KTCodePreview title="Báo cáo tiền">
      <template v-slot:toolbar>
        <div class="row">
          <div>
            <b-dropdown
              size="sm"
              id="dropdown-actions"
              right
            >
              <template slot="button-content">
                <i
                  style="font-size: 1rem"
                  class="fas fa-cog"
                ></i>
                <span class="font-weight-bolder">Thao tác</span>
              </template>
              <b-dropdown-item v-if="checkPermission('WARRANTY_REPORT_SUMMARY_EXPORT')" @click="onExportExcel">
                <span>
                  <i
                    style="font-size: 1rem"
                    class="far fa-file-excel"
                  ></i>
                  &nbsp; Xuất Excel
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </template>
      <template v-slot:preview>
        <!-- Filter -->
        <b-row class="mb-3">
          <b-col cols="2">
            <b-input-group>
              <date-picker
                v-model="filter.dateFrom"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                placeholder="Từ ngày"
              />
            </b-input-group>
          </b-col>
          <b-col cols="2">
            <b-input-group>
              <date-picker
                v-model="filter.dateTo"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                placeholder="Đến ngày"
              />
            </b-input-group>
          </b-col>
          <b-col cols="4">
            <BDropdownCustom
              v-model="filter.store"
              :options="optionStores"
              placeholder="chi nhánh"
              text-prop="label"
              value-prop="id"
              :match-key="['label', 'shortName']"
              :searchable="true"
              :limit="10"
            />
          </b-col>
          <b-col cols="2">
            <b-form-select
              v-model="filter.warrantyServiceId"
              size="sm"
              class="form-control"
              :options="serviceTypeOptions"
              value-field="id"
              text-field="name"
            >
              <template #first>
                <b-form-select-option :value="null">Tất cả dịch vụ</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
          <b-col cols="2">
            <BDropdownCustom
              :value="statusSelectedObjects"
              :options="statusOptions"
              placeholder="Trạng thái"
              :searchable="true"
              :match-key="['name', 'id']"
              text-prop="name"
              value-prop="id"
              @valueChange="onStatusChange"
            />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="2">
            <b-form-input
              v-model.trim="filter.id"
              size="sm"
              placeholder="ID phiếu"
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col cols="2">
            <b-form-input
              v-model.trim="filter.code"
              size="sm"
              placeholder="Mã phiếu"
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <b-form-input
              v-model="filter.imeiCode"
              size="sm"
              placeholder="Tìm theo IMEI"
              trim
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <b-form-input
              v-model="filter.productName"
              size="sm"
              placeholder="Tìm theo tên sản phẩm"
              trim
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col cols="2">
            <b-form-select
              v-model="filter.companyId"
              size="sm"
              class="form-control"
              :options="listCompany"
              value-field="id"
              text-field="name"
            />
          </b-col>
          <b-col cols="2">
            <BDropdownCustom
              :value="criteriaSelectedObjects"
              :options="criteriaOptions"
              placeholder="Đặc điểm"
              :searchable="true"
              :match-key="['name']"
              text-prop="name"
              value-prop="id"
              @valueChange="onCriteriaChange"
            />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="2">
            <EmployeeSearch
              :value="filter.primaryTechnicianName"
              :employee-id="filter.primaryTechnicianId"
              placeholder="KTV sửa chữa"
              @employee-selected="onRepairTechnicianSelected"
            />
          </b-col>
          <b-col cols="2">
            <EmployeeSearch
              :value="filter.inspectedByEmployeeName"
              :employee-id="filter.inspectedByEmployeeId"
              placeholder="KTV kiểm tra"
              @employee-selected="onInspectionTechnicianSelected"
            />
          </b-col>
          <b-col cols="4">
            <Autosuggest
              :model="selectedCreditAcc"
              :suggestions="filteredCreditAccOptions"
              placeholder="tài khoản quẹt thẻ"
              :limit="10"
              @select="onCreditAccSelected"
              @change="onInputCreditAccChange"
            />
          </b-col>
          <b-col cols="4">
            <b-form-input
              v-model.trim="filter.paymentCode"
              size="sm"
              placeholder="Mã giao dịch"
              @keyup.enter="onFilter"
            />
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col
            cols="12"
            class="d-flex align-items-center"
            style="gap: 8px"
          >
            <b-button
              variant="primary"
              size="sm"
              @click="onFilter"
              style="width: 70px"
            >
              Lọc
            </b-button>
            <b-button
              style="font-weight: 600; width: auto; min-width: 120px"
              variant="secondary"
              size="sm"
              @click="onReset"
              :disabled="isLoading || activeFilterCount === 0"
              class="ml-2"
            >
              Xóa bộ lọc
              <span v-if="activeFilterCount > 0">({{ activeFilterCount }})</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- KPI cards -->
        <b-row class="mb-5 mt-4">
          <b-col
            cols="12"
            md="4"
            class="mb-2 mb-md-0"
          >
            <b-card class="border-left-primary h-100 report-summary-kpi-card">
              <div
                class="rounded p-1 d-inline-flex align-items-center justify-content-center bg-light text-primary mb-1"
              >
                <i class="fas fa-coins"></i>
              </div>
              <template v-if="isLoading">
                <b-skeleton width="60%" />
                <b-skeleton width="80%" height="1.5rem" class="mt-2" />
              </template>
              <h6 v-else class="mb-0 font-weight-bold text-primary">
                {{ formatMoney(kpi.totalRevenue) }} ₫
              </h6>
              <small class="text-muted">Tổng tiền thu</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mb-2 mb-md-0"
          >
            <b-card class="border-left-success h-100 report-summary-kpi-card">
              <div
                class="rounded p-1 d-inline-flex align-items-center justify-content-center bg-light text-success mb-1"
              >
                <i class="fas fa-clipboard-list"></i>
              </div>
              <template v-if="isLoading">
                <b-skeleton width="40%" />
                <b-skeleton width="60%" height="1.5rem" class="mt-2" />
              </template>
              <h6 v-else class="mb-0 font-weight-bold text-success">{{ kpi.totalTickets }}</h6>
              <small class="text-muted">Tổng phiếu tiếp nhận bảo hành</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-card class="border-left-danger h-100 report-summary-kpi-card">
              <div
                class="rounded p-1 d-inline-flex align-items-center justify-content-center bg-light text-danger mb-1"
              >
                <i class="fas fa-exclamation-circle"></i>
              </div>
              <template v-if="isLoading">
                <b-skeleton width="40%" />
                <b-skeleton width="60%" height="1.5rem" class="mt-2" />
              </template>
              <h6 v-else class="mb-0 font-weight-bold text-danger">{{ kpi.unpaidCount }}</h6>
              <small class="text-muted">Phiếu chưa đủ tiền (thất thoát)</small>
            </b-card>
          </b-col>
        </b-row>

        <!-- Bảng chi tiết -->
        <b-card
          no-body
          class="border-0 shadow-none mb-0 mt-3"
        >
          <b-card-body class="p-0 table-container">
            <b-table
              :items="detailPaginated"
              :fields="detailFields"
              bordered
              hover
              small
              :busy="isLoading"
              class="table-bordered table-hover table-sm report-data-table"
              :style="{ minWidth: tableMinWidth + 'px' }"
              show-empty
              empty-text="Chưa có dữ liệu"
            >
              <!-- ── GROUP HEADER ── -->
              <template #thead-top>
                <b-tr class="group-header-row report-table-group-header">
                  <!-- Cột định danh: ID, Mã phiếu, Ngày TN, Khách hàng, Chi nhánh, Sản phẩm/IMEI, Loại dịch vụ, Phương thức, Trạng thái, Mô tả lỗi, Linh kiện -->
                  <b-th
                    colspan="13"
                    class="group-header text-center"
                  >
                    THÔNG TIN
                  </b-th>

                  <!-- Nhóm 1: Báo giá & Tạm tính (estimatedCost, estimateTotalAmount) -->
                  <b-th
                    colspan="2"
                    class="group-header text-center"
                  >
                    BÁO GIÁ &amp; TẠM TÍNH
                  </b-th>

                  <!-- Nhóm 2: Thực thu (actualCost, diffEstimate, diffActual) -->
                  <b-th
                    colspan="3"
                    class="group-header text-center"
                  >
                    THỰC THU
                  </b-th>

                  <!-- Nhóm 3: Outsource (outsourceEstimateCost, outsourceActualCost, diffOutsource) -->
                  <b-th
                    colspan="3"
                    class="group-header text-center"
                  >
                    SỬA NGOÀI
                  </b-th>

                  <!-- Thu tiền + Cảnh báo -->
                  <b-th
                    colspan="4"
                    class="group-header text-center"
                  >
                    THÔNG TIN CHUNG
                  </b-th>
                </b-tr>
              </template>

              <template #table-busy>
                <vcl-table
                  :speed="5"
                  :animate="true"
                  :columns="10"
                />
              </template>

              <template #cell(id)="row">
                <span
                  class="text-primary font-weight-bold cursor-pointer"
                  @click="linkToWarranty(row.item.id)"
                  v-b-tooltip.hover.top
                  title="Chi tiết bảo hành"
                  >{{ row.item.id || '-' }}</span
                >
              </template>

              <template #cell(code)="row">
                <span
                  class="text-primary font-weight-bold cursor-pointer"
                  @click="linkToWarranty(row.item.id)"
                  v-b-tooltip.hover.top
                  title="Chi tiết bảo hành"
                >{{ row.item.code || '-' }} 
                <CompanyCodeBadge
                  class="ml-1"
                  :company-code="row.item.companyCode"
                  :company-name="row.item.companyName"
                />
              </span>
              </template>

              <template #cell(receptionDate)="row">
                <span>{{ convertToDateSlash(row.item.createdAt, DATE_FORMAT) }}</span>
              </template>

              <template #cell(customer)="row">
                <div
                  v-if="row.item.customerName"
                  class="font-weight-bold text-primary cursor-pointer hover-link"
                  @click="linkToCustomer(row.item)"
                >
                  {{ row.item.customerName }}
                </div>
                <div v-else>-</div>
                <span
                  v-if="row.item.customerPhone"
                  class="text-primary cursor-pointer hover-link"
                  @click="linkToCustomer(row.item)"
                  style="font-size: 0.9em;"
                >
                  {{ row.item.customerPhone }}
                </span>
              </template>

              <template #cell(productName)="row">
                <div class="font-weight-bold">{{ row.item.productName || '-' }}</div>
                <span class="text-success">{{ row.item.imeiCode }}</span>
              </template>

              <template #cell(method)="row">
                <span>{{ row.item.hasOutsource ? 'Outsource' : '' }}</span>
              </template>

              <template #cell(statusName)="row">
                <WarrantyStatusBadge
                  :status="row.item.status"
                  :status-name="row.item.statusName"
                />
              </template>

              <template #cell(isCannotRepair)="row">
                <b-badge v-if="row.item.isCannotRepair" variant="danger">Không sửa được</b-badge>
                <span v-else></span>
              </template>

              <template #cell(isDisagreePrice)="row">
                <b-badge v-if="row.item.isDisagreePrice" variant="warning">Không đồng ý giá</b-badge>
                <span v-else></span>
              </template>

              <template #cell(issueDescription)="row">
                <span
                  class="text-truncate d-inline-block"
                  style="max-width: 200px"
                  >{{ row.item.issueDescription }}</span
                >
              </template>

              <template #cell(parts)="{ item }">
                <div class="parts-stack">
                  <div
                    v-for="(part, idx) in item.replaceProducts"
                    :key="idx"
                    class="stack-row"
                  >
                    <div>
                      <span class="row-name">Tên hàng: &nbsp;{{ part.productName }}</span
                      ><br />
                      <span class="row-qty">Số lượng: &nbsp;{{ part.quantity }}</span
                      ><br />
                      <span class="text-danger">Chiết khấu: &nbsp;{{ formatMoney(part.discountAmount) }}</span>
                      <br />
                      <span class="row-total-amount">Tổng tiền: &nbsp;{{ formatMoney(part.totalAmount) }}</span>
                    </div>
                    <a
                      v-if="part.stockSlipCode"
                      href="#"
                      class="row-slip row-slip--below"
                      @click.prevent="openStockSlip(part.stockSlipId)"
                      v-b-tooltip.hover
                      :title="`Phiếu xuất ${part.stockSlipCode}`"
                      >{{ part.stockSlipCode }}</a
                    >
                  </div>
                </div>
              </template>

              <!-- Nhóm 1 -->
              <template #cell(estimatedCost)="row">
                <div class="text-right">{{ formatMoney(row.item.estimatedCost) }}</div>
              </template>
              <template #cell(estimateTotalAmount)="row">
                <div class="text-right">{{ formatMoney(row.item.estimateTotalAmount) }}</div>
              </template>

              <!-- Nhóm 2 -->
              <template #cell(actualCost)="row">
                <div class="text-right text-primary font-weight-bold">
                  {{ formatMoney(row.item.actualCost) }}
                </div>
              </template>
              <template #cell(diffEstimate)="row">
                <div
                  class="text-right font-weight-bold"
                  :class="(row.item.diffEstimate || 0) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatMoney(row.item.diffEstimate) }}
                </div>
              </template>
              <template #cell(diffActual)="row">
                <div
                  class="text-right font-weight-bold"
                  :class="(row.item.diffActual || 0) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatMoney(row.item.diffActual) }}
                </div>
              </template>

              <!-- Nhóm 3 -->
              <template #cell(outsourceEstimateCost)="row">
                <div class="text-right">{{ formatMoney(row.item.outsourceEstimateCost) }}</div>
              </template>
              <template #cell(outsourceActualCost)="row">
                <div class="text-right">{{ formatMoney(row.item.outsourceActualCost) }}</div>
              </template>
              <template #cell(diffOutsource)="row">
                <div
                  class="text-right font-weight-bold"
                  :class="(row.item.diffOutsource || 0) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatMoney(row.item.diffOutsource) }}
                </div>
              </template>

              <template #cell(paidAmount)="row">
                <span
                  class="font-weight-bold"
                  :class="
                    row.item.paidAmount < row.item.actualCost ? 'text-danger' : 'text-success'
                  "
                >
                  {{ formatMoney(row.item.paidAmount) }} ₫
                </span>
              </template>

              <template #cell(discountAmount)="row">
                <div class="text-right">{{ formatMoney(row.item.discountAmount) }}</div>
              </template>

              <template #cell(alerts)="row">
                <template v-if="row.item.alerts && row.item.alerts.length">
                  <div
                    v-for="(alert, idx) in row.item.alerts"
                    :key="idx"
                    class="small mb-1"
                    :class="{
                      'text-warning': alert.type === 'unpaid',
                      'text-danger': alert.type === 'mismatch',
                      'text-secondary': !alert.type || !['unpaid', 'mismatch'].includes(alert.type),
                    }"
                  >
                    <i
                      v-if="alert.type === 'unpaid'"
                      class="fas fa-exclamation-circle mr-1"
                    ></i>
                    <i
                      v-else-if="alert.type === 'mismatch'"
                      class="fas fa-exclamation-triangle mr-1"
                    ></i>
                    {{ alert.message }}
                  </div>
                </template>
                <div
                  v-else
                  class="small text-success"
                >
                  <i class="fas fa-check"></i> Khớp
                </div>
              </template>
              <template #cell(supportCost)="row">
                <div class="text-right">{{ formatMoney(row.item.supportCost) }}</div>
              </template>
            </b-table>
          </b-card-body>

          <b-card-footer class="d-flex justify-content-between align-items-center py-2">
            <span class="font-weight-bold">
              Hiển thị {{ detailFrom }} - {{ detailTo }} / {{ totalRow }} phiếu
            </span>
            <BPaginationNavCustom
              :number-of-pages="numberOfPage"
              :current-page="currentPage"
              :total-page="numberOfPage"
              @page-change="onPageChange"
            />
          </b-card-footer>
        </b-card>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import Loader from '@/view/content/Loader.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import { mapGetters } from 'vuex'
import { formatDBDate, convertToDateSlash, DATE_FORMAT, getStartOfMonth } from '@/utils/date'
import { convertPrice } from '@/utils/common'
import apiCommonMixin from '@/view/mixins/api-common.mixins'
import {
  getWarrantyServices,
  getWarrantyReports,
  getWarrantyReportsSummary,
  exportWarrantyReportExcel,
} from '../utils/warrantyApi'
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'
import WarrantyStatusBadge from '../components/WarrantyStatusBadge.vue'
import CompanyCodeBadge from '@/view/components/common/CompanyCodeBadge.vue'
import EmployeeSearch from '@/view/components/common/EmployeeSearch.vue'
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue'
import ApiService from '@/core/services/api.service'
import { removeAccents } from '@/utils/common'
import { ACCOUNTANT_TYPE } from '@/utils/enum'
import { VclTable } from 'vue-content-loading'
import './WarrantyReportSummaryStyles.css'
import { WARRANTY_STATUS_OPTIONS } from '@/utils/constants'
import permissionMixin from '@/view/mixins/permission.mixin'

const DETAIL_PAGE_SIZE = 10

export default {
  mixins: [apiCommonMixin, permissionMixin],
  components: {
    KTCodePreview,
    Loader,
    BDropdownCustom,
    BPaginationNavCustom,
    WarrantyStatusBadge,
    CompanyCodeBadge,
    VclTable,
    EmployeeSearch,
    Autosuggest,
  },
  data() {
    return {
      DATE_FORMAT,
      filter: {
        dateFrom: getStartOfMonth(),
        dateTo: null,
        store: [],
        id: null,
        code: null,
        warrantyServiceId: null,
        status: [],
        imeiCode: '',
        productName: '',
        criteriaIds: [],
        primaryTechnicianId: null,
        primaryTechnicianName: '',
        inspectedByEmployeeId: null,
        inspectedByEmployeeName: '',
        companyId: null,
        creditUnitIds: [],
        paymentCode: null,
      },
      selectedCreditAcc: '',
      filteredCreditAccOptions: [],
      creditAccOptions: [{ data: [] }],
      optionStores: [],
      listCompany: [],
      serviceTypeOptions: [],
      statusOptions: WARRANTY_STATUS_OPTIONS,
      criteriaOptions: [
        { id: 'HAS_DEBT', name: 'Còn công nợ' },
        { id: 'NO_DEBT', name: 'Hết công nợ' },
        { id: 'CANNOT_REPAIR', name: 'Không sửa được' },
        { id: 'DISAGREE_PRICE', name: 'Không đồng ý giá' },
      ],
      rawDataset: [],
      totalRow: 0,
      totalPage: 1,
      summaryKpi: { totalRevenue: 0, warrantyCount: 0, unpaidCount: 0 },
      isLoading: false,
      currentPage: 1,

      detailFields: [
        // ── Định danh (colspan 12 trong thead-top) ──
        {
          key: 'id',
          label: 'ID',
          thClass: 'text-center',
          thStyle: { width: '60px', minWidth: '60px', tdClass: 'text-center' },
        },
        {
          key: 'code',
          label: 'Mã phiếu',
          thClass: 'text-center',
          thStyle: { width: '200px', minWidth: '200px' },
        },
        { key: 'receptionDate', label: 'Ngày TN', thClass: 'text-center', thStyle: { width: '90px', minWidth: '90px' } },
        {
          key: 'customer',
          label: 'Khách hàng',
          thClass: 'text-center',
          thStyle: { width: '150px', minWidth: '150px' },
        },
        { key: 'storeName', label: 'Chi nhánh', thClass: 'text-center', thStyle: { width: '120px', minWidth: '120px' } },
        {
          key: 'productName',
          label: 'Sản phẩm / IMEI',
          thClass: 'text-center',
          thStyle: { width: '160px', minWidth: '160px' },
        },
        {
          key: 'warrantyServiceName',
          label: 'Loại dịch vụ',
          thClass: 'text-center',
          thStyle: { width: '110px', minWidth: '110px' },
        },
        { key: 'method', label: 'Phương thức', thClass: 'text-center', thStyle: { width: '90px', minWidth: '90px' } },
        {
          key: 'statusName',
          label: 'Trạng thái',
          thClass: 'text-center',
          thStyle: { width: '110px', minWidth: '110px' },
          tdClass: 'text-center',
        },
        {
          key: 'isCannotRepair',
          label: 'Không sửa được',
          thClass: 'text-center',
          thStyle: { width: '130px', minWidth: '130px' },
          tdClass: 'text-center',
        },
        {
          key: 'isDisagreePrice',
          label: 'Không đồng ý giá',
          thClass: 'text-center',
          thStyle: { width: '130px', minWidth: '130px' },
          tdClass: 'text-center',
        },
        {
          key: 'issueDescription',
          label: 'Mô tả lỗi',
          thClass: 'text-center',
          thStyle: { width: '150px', minWidth: '150px' },
        },
        {
          key: 'parts',
          label: 'Linh kiện / Dịch vụ',
          thClass: 'text-center',
          thStyle: { width: '220px', minWidth: '220px' },
        },

        // ── Nhóm 1: Báo giá & Tạm tính (colspan 2) ──
        {
          key: 'estimatedCost',
          label: 'Báo giá',
          thClass: 'text-center',
          thStyle: { width: '100px', minWidth: '100px' },
          tdClass: 'text-right',
        },
        {
          key: 'estimateTotalAmount',
          label: 'Tổng chi phí',
          thClass: 'text-center',
          thStyle: { width: '120px', minWidth: '120px' },
          tdClass: 'text-right',
        },

        // ── Nhóm 2: Thực thu (colspan 3) ──
        {
          key: 'actualCost',
          label: 'Tiền thu khách',
          thClass: 'text-center',
          thStyle: { width: '110px', minWidth: '110px' },
          tdClass: 'text-right',
        },
        {
          key: 'diffEstimate',
          label: 'Chênh lệch báo giá',
          thClass: 'text-center',
          thStyle: { width: '110px', minWidth: '110px' },
          tdClass: 'text-right',
        },
        {
          key: 'diffActual',
          label: 'Chênh lệch thực xuất',
          thClass: 'text-center',
          thStyle: { width: '110px', minWidth: '110px' },
          tdClass: 'text-right',
        },

        // ── Nhóm 3: Outsource (colspan 3) ──
        {
          key: 'outsourceEstimateCost',
          label: 'Chi phí dự kiến',
          thClass: 'text-center',
          thStyle: { width: '130px', minWidth: '130px' },
          tdClass: 'text-right',
        },
        {
          key: 'outsourceActualCost',
          label: 'Chi phí thực tế',
          thClass: 'text-center',
          thStyle: { width: '130px', minWidth: '130px' },
          tdClass: 'text-right',
        },
        {
          key: 'diffOutsource',
          label: 'Chênh lệch',
          thClass: 'text-center',
          thStyle: { width: '110px', minWidth: '110px' },
          tdClass: 'text-right',
        },
        {
          key: 'paidAmount',
          label: 'Thực thu',
          thClass: 'text-center',
          thStyle: { width: '100px', minWidth: '100px' },
          tdClass: 'text-right align-middle',
        },
        {
          key: 'discountAmount',
          label: 'Tổng chiết khấu',
          thClass: 'text-center',
          thStyle: { width: '100px', minWidth: '100px' },
          tdClass: 'text-right align-middle',
        },
        {
          key: 'supportCost',
          label: 'Phí hỗ trợ bảo hành',
          thClass: 'text-center',
          thStyle: { width: '100px', minWidth: '100px' },
          tdClass: 'text-right align-middle',
        },
        { key: 'alerts', label: 'Cảnh báo', thClass: 'text-center', thStyle: { width: '130px', minWidth: '130px' } },
      ],
    }
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    dpConfigs() {
      return {
        date: {
          format: 'DD/MM/YYYY',
          useCurrent: false,
          showClear: true,
          showClose: true,
          locale: 'vi',
          allowInputToggle: true,
        },
      }
    },
    mappedList() {
      return this.rawDataset
    },
    activeFilterCount() {
      const f = this.filter
      let count = 0
      if (f.dateFrom) count++
      if (f.dateTo) count++
      if (f.id != null && String(f.id).trim()) count++
      if (f.code != null && String(f.code).trim()) count++
      if ((Array.isArray(f.store) ? f.store : []).length > 0) count++
      if (f.warrantyServiceId) count++
      if ((Array.isArray(f.status) ? f.status : []).length > 0) count++
      if (f.imeiCode && f.imeiCode.trim()) count++
      if (f.productName && f.productName.trim()) count++
      if ((Array.isArray(f.criteriaIds) ? f.criteriaIds : []).length > 0) count++
      if (f.primaryTechnicianId) count++
      if (f.inspectedByEmployeeId) count++
      if (f.companyId) count++
      if ((Array.isArray(f.creditUnitIds) ? f.creditUnitIds : []).length > 0) count++
      if (f.paymentCode && String(f.paymentCode).trim()) count++
      return count
    },
    criteriaSelectedObjects() {
      const ids = this.filter.criteriaIds || []
      return this.criteriaOptions.filter((c) => ids.includes(c.id))
    },
    statusSelectedObjects() {
      const ids = this.filter.status || []
      return this.statusOptions.filter((s) => ids.includes(s.id))
    },
    kpi() {
      return {
        totalTickets: this.summaryKpi.warrantyCount ?? 0,
        totalRevenue: this.summaryKpi.totalRevenue ?? 0,
        unpaidCount: this.summaryKpi.unpaidCount ?? 0,
      }
    },
    numberOfPage() {
      return Math.max(1, this.totalPage)
    },
    tableMinWidth() {
      return this.detailFields.reduce((sum, f) => {
        const w = parseInt(f.thStyle?.minWidth || f.thStyle?.width || '120px')
        return sum + w
      }, 0)
    },
    detailPaginated() {
      return this.mappedList
    },
    detailFrom() {
      if (this.totalRow === 0) return 0
      return (this.currentPage - 1) * DETAIL_PAGE_SIZE + 1
    },
    detailTo() {
      return (this.currentPage - 1) * DETAIL_PAGE_SIZE + this.rawDataset.length
    },
  },
  async created() {
    this.fetchListReport()
    this.fetchSummaryKpi()
    this.fetchStores()
    this.fetchServiceTypes()
    this.fetchCompany()
    await this.fetchCreditAccounts()
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Báo cáo tiền' }])
  },
  methods: {
    convertToDateSlash,
    linkToCustomer(row) {
      if (!row?.customerId && !row?.customerPhone) return
      const route = this.$router.resolve({
        name: 'update-customer',
        query: { id: row.customerId, phoneNo: row.customerPhone },
      })
      window.open(route.href, '_blank')
    },
    linkToWarranty(id) {
      const route = this.$router.resolve({ name: 'warranty-dispatch-info', query: { id } })
      window.open(route.href, '_blank')
    },
    openStockSlip(id) {
      const route = this.$router.resolve({ name: 'edit-inventory-dispatch', query: { id } })
      window.open(route.href, '_blank')
    },
    openWarrantyDetail(id) {
      const route = this.$router.resolve({ name: 'warranty-dispatch-info', query: { id } })
      window.open(route.href, '_blank')
    },
    formatMoney(val) {
      return convertPrice(Number(val) || 0)
    },
    mapDataTreeSelect(items = [], keys = ['id', 'name', 'shortName']) {
      if (!items || items.length === 0) return []
      const [idKey, nameKey, shortNameKey] = keys
      return items.map((item) => ({
        id: item[idKey],
        label: item[nameKey] || item[shortNameKey],
        shortName: item[shortNameKey] || item[nameKey],
      }))
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchListReport()
    },
    async fetchStores() {
      try {
        const { stores } = await this.fetchStoreByUser()
        this.optionStores = this.mapDataTreeSelect(stores, ['id', 'name', 'shortName'])
      } catch (e) {
        this.optionStores = []
      }
    },
    async fetchServiceTypes() {
      try {
        const list = await getWarrantyServices()
        this.serviceTypeOptions = (list || []).map((s) => ({
          id: s.id,
          name: s.name || s.serviceName,
        }))
      } catch (e) {
        this.serviceTypeOptions = []
      }
    },
    async fetchCompany() {
      const list = await this.fetchCompanies()
      this.listCompany = [{ id: null, name: 'Tất cả doanh nghiệp' }, ...list]
    },
    getParamFilters() {
      const store =
        Array.isArray(this.filter.store) && this.filter.store.length ? this.filter.store[0] : null
      const storeId = store && store.id ? store.id : null
      return {
        page: this.currentPage,
        limit: DETAIL_PAGE_SIZE,
        storeId: storeId || undefined,
        startDate: formatDBDate(this.filter.dateFrom),
        endDate: formatDBDate(this.filter.dateTo),
        id:
          this.filter.id != null && String(this.filter.id).trim()
            ? String(this.filter.id).trim()
            : undefined,
        code:
          this.filter.code != null && String(this.filter.code).trim()
            ? String(this.filter.code).trim()
            : undefined,
        status:
          Array.isArray(this.filter.status) && this.filter.status.length
            ? this.filter.status
            : undefined,
        warrantyServiceId: this.filter.warrantyServiceId || undefined,
        imeiCode: this.filter.imeiCode?.trim() || undefined,
        productName: this.filter.productName?.trim() || undefined,
        criteriaIds:
          Array.isArray(this.filter.criteriaIds) && this.filter.criteriaIds.length
            ? this.filter.criteriaIds
            : undefined,
        primaryTechnicianId: this.filter.primaryTechnicianId || undefined,
        inspectedByEmployeeId: this.filter.inspectedByEmployeeId || undefined,
        companyId: this.filter.companyId || undefined,
        creditUnitIds: (this.filter.creditUnitIds || []).map((u) => u.id),
        paymentCode: this.filter.paymentCode?.trim() || undefined,
      }
    },
    getSummaryParams() {
      const store =
        Array.isArray(this.filter.store) && this.filter.store.length ? this.filter.store[0] : null
      const storeId = store && store.id ? store.id : null
      return {
        storeId: storeId || undefined,
        startDate: formatDBDate(this.filter.dateFrom),
        endDate: formatDBDate(this.filter.dateTo),
        id:
          this.filter.id != null && String(this.filter.id).trim()
            ? String(this.filter.id).trim()
            : undefined,
        code:
          this.filter.code != null && String(this.filter.code).trim()
            ? String(this.filter.code).trim()
            : undefined,
        status:
          Array.isArray(this.filter.status) && this.filter.status.length
            ? this.filter.status
            : undefined,
        warrantyServiceId: this.filter.warrantyServiceId || undefined,
        imeiCode: this.filter.imeiCode?.trim() || undefined,
        productName: this.filter.productName?.trim() || undefined,
        criteriaIds:
          Array.isArray(this.filter.criteriaIds) && this.filter.criteriaIds.length
            ? this.filter.criteriaIds
            : undefined,
        primaryTechnicianId: this.filter.primaryTechnicianId || undefined,
        inspectedByEmployeeId: this.filter.inspectedByEmployeeId || undefined,
        companyId: this.filter.companyId || undefined,
        creditUnitIds: (this.filter.creditUnitIds || []).map((u) => u.id),
        paymentCode: this.filter.paymentCode?.trim() || undefined,
      }
    },
    onStatusChange(selectedObjects) {
      this.filter.status = (selectedObjects || []).map((o) => o.id)
    },
    onCriteriaChange(selectedObjects) {
      this.filter.criteriaIds = (selectedObjects || []).map((o) => o.id)
    },
    async fetchCreditAccounts() {
      const res = await ApiService.query('accountants/getByType', { params: { type: ACCOUNTANT_TYPE.CREDIT, pageSize: 1000 } })
      const raw = res.data?.data
      const items = Array.isArray(raw) ? raw : []
      const mapped = items
        .filter((item) => item.level >= 2)
        .map((item) => ({ id: item.id, name: item.name }))
      this.creditAccOptions = [{ data: mapped }]
      this.filteredCreditAccOptions = [...mapped]
    },
    onCreditAccSelected(option) {
      this.selectedCreditAcc = option.item.name
      this.filter.creditUnitIds = option.item.id ? [option.item] : []
    },
    onInputCreditAccChange(text) {
      this.selectedCreditAcc = text
      this.filter.creditUnitIds = []
      const filteredData = this.creditAccOptions[0].data
        .filter((item) =>
          removeAccents(item.name).toLowerCase().indexOf(removeAccents(text || '').toLowerCase()) > -1
        )
        .slice(0, 10)
      this.filteredCreditAccOptions = [...filteredData]
    },
    onRepairTechnicianSelected(employee) {
      this.filter.primaryTechnicianId = employee ? employee.id : null
      this.filter.primaryTechnicianName = employee ? employee.name : ''
    },
    onInspectionTechnicianSelected(employee) {
      this.filter.inspectedByEmployeeId = employee ? employee.id : null
      this.filter.inspectedByEmployeeName = employee ? employee.name : ''
    },
    async fetchSummaryKpi() {
      const params = this.getSummaryParams()
      const summaryRes = await getWarrantyReportsSummary(params)
      this.summaryKpi = {
        totalRevenue: summaryRes.totalRevenue ?? 0,
        warrantyCount: summaryRes.warrantyCount ?? 0,
        unpaidCount: summaryRes.unpaidCount ?? 0,
      }
    },
    async fetchListReport() {
      this.isLoading = true
      const params = this.getParamFilters()
      const listRes = await getWarrantyReports(params)
      this.rawDataset = listRes.dataset || []
      this.totalRow = listRes.totalRow ?? 0
      this.totalPage = Math.max(1, listRes.totalPage ?? 1)
      this.isLoading = false
    },
    onFilter() {
      this.currentPage = 1
      this.fetchListReport()
      this.fetchSummaryKpi()
    },
    async onExportExcel() {
      const params = this.getSummaryParams()
      await exportWarrantyReportExcel(params)
    },
    onReset() {
      this.filter = {
        dateFrom: null,
        dateTo: null,
        store: [],
        id: null,
        code: null,
        warrantyServiceId: null,
        status: [],
        imeiCode: '',
        productName: '',
        criteriaIds: [],
        primaryTechnicianId: null,
        primaryTechnicianName: '',
        inspectedByEmployeeId: null,
        inspectedByEmployeeName: '',
        companyId: null,
        creditUnitIds: [],
        paymentCode: null,
      }
      this.selectedCreditAcc = ''
      this.filteredCreditAccOptions = [...(this.creditAccOptions[0]?.data || [])]
      this.currentPage = 1
      this.fetchListReport()
      this.fetchSummaryKpi()
    },
  },
}
</script>